import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useSilhouetteExtractor } from "./SilhouetteExtractorContext";

export function SilhouetteExtractorViewer() {
    const {
        model,
        modelName,
        isLoadingModel,
        loadError,
        cameraParams,
        generatedSvg,
        viewMode,
        setViewMode,
        isGenerating,
        generateSilhouette,
        canExport,
        handleDownload,
        cameraRef,
    } = useSilhouetteExtractor();

    const mountRef = useRef<HTMLDivElement>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const controlsRef = useRef<OrbitControls | null>(null);
    const animFrameRef = useRef<number>(0);
    const modelInSceneRef = useRef<THREE.Object3D | null>(null);

    // Mount: create renderer, scene, camera, controls, animation loop
    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const width = Math.max(mount.clientWidth, 1);
        const height = Math.max(mount.clientHeight, 1);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(width, height);
        renderer.setClearColor(0x1e1e2e, 1);
        mount.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const ambient = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambient);
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.9);
        dirLight.position.set(5, 10, 7);
        scene.add(dirLight);

        const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 1000);
        camera.position.set(0, 0, 5);
        cameraRef.current = camera;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controlsRef.current = controls;

        const animate = () => {
            animFrameRef.current = requestAnimationFrame(animate);
            controls.update();
            if (cameraRef.current) {
                renderer.render(scene, cameraRef.current);
            }
        };
        animate();

        const ro = new ResizeObserver(() => {
            const w = Math.max(mount.clientWidth, 1);
            const h = Math.max(mount.clientHeight, 1);
            renderer.setSize(w, h);
            const cam = cameraRef.current;
            if (cam instanceof THREE.PerspectiveCamera) {
                cam.aspect = w / h;
                cam.updateProjectionMatrix();
            } else if (cam instanceof THREE.OrthographicCamera) {
                const aspect = w / h;
                const frust = cam.top;
                cam.left = -frust * aspect;
                cam.right = frust * aspect;
                cam.updateProjectionMatrix();
            }
        });
        ro.observe(mount);

        return () => {
            cancelAnimationFrame(animFrameRef.current);
            ro.disconnect();
            controls.dispose();
            renderer.dispose();
            if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
            rendererRef.current = null;
            sceneRef.current = null;
            controlsRef.current = null;
            cameraRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync model into Three.js scene
    useEffect(() => {
        const scene = sceneRef.current;
        if (!scene) return;

        if (modelInSceneRef.current) {
            scene.remove(modelInSceneRef.current);
            modelInSceneRef.current = null;
        }

        if (!model) return;

        // Model is already normalized (centered, scaled) at load time in context.
        const clone = model.clone(true);
        scene.add(clone);
        modelInSceneRef.current = clone;

        if (controlsRef.current) {
            controlsRef.current.target.set(0, 0, 0);
            controlsRef.current.update();
        }
    }, [model]);

    // Sync camera params into Three.js camera
    useEffect(() => {
        const mount = mountRef.current;
        if (!mount || !rendererRef.current || !controlsRef.current) return;

        const { fov, projectionType, distance } = cameraParams;
        const oldCamera = cameraRef.current;
        const controls = controlsRef.current;
        const target = controls.target.clone();

        // Compute orbit direction from old camera position
        const dir = new THREE.Vector3(0, 0, 1);
        if (oldCamera) {
            dir.subVectors(oldCamera.position, target);
            if (dir.lengthSq() > 1e-10) dir.normalize();
        }

        const w = Math.max(mount.clientWidth, 1);
        const h = Math.max(mount.clientHeight, 1);
        const aspect = w / h;
        let newCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera;

        if (projectionType === "perspective") {
            const cam = new THREE.PerspectiveCamera(fov, aspect, 0.01, 1000);
            cam.position.copy(target).addScaledVector(dir, distance);
            cam.lookAt(target);
            newCamera = cam;
        } else {
            const frust = distance * 0.5;
            const cam = new THREE.OrthographicCamera(
                -frust * aspect, frust * aspect,
                frust, -frust,
                0.01, 1000,
            );
            cam.position.copy(target).addScaledVector(dir, distance);
            cam.lookAt(target);
            newCamera = cam;
        }

        cameraRef.current = newCamera;
        controls.object = newCamera;
        controls.target.copy(target);
        controls.update();
    }, [cameraParams, cameraRef]);

    return (
        <div className="panel-viewer silhouette-extractor-viewer">
            <div className="path-tool-preview">
                <div style={{ display: "flex", gap: 4, padding: "4px 8px" }}>
                    <button
                        type="button"
                        className={`zoom-button${viewMode === "preview" ? " active" : ""}`}
                        onClick={() => setViewMode("preview")}
                    >
                        3D Preview
                    </button>
                    <button
                        type="button"
                        className={`zoom-button${viewMode === "result" ? " active" : ""}`}
                        onClick={() => setViewMode("result")}
                        disabled={!generatedSvg}
                    >
                        SVG Result
                    </button>
                </div>

                {/* WebGL canvas — always mounted; hidden when showing result */}
                <div
                    ref={mountRef}
                    style={{
                        display: viewMode === "preview" ? "block" : "none",
                        width: "100%",
                        flex: 1,
                        minHeight: 380,
                        overflow: "hidden",
                    }}
                />

                {viewMode === "result" && generatedSvg && (
                    <div className="svg-scroll-wrapper" style={{ flex: 1 }}>
                        <div
                            className="svg-container"
                            dangerouslySetInnerHTML={{ __html: generatedSvg }}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                        />
                    </div>
                )}

                {viewMode === "preview" && !model && !isLoadingModel && (
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "#888",
                            fontSize: 13,
                            pointerEvents: "none",
                            textAlign: "center",
                        }}
                    >
                        Load a GLTF/GLB model to begin
                    </div>
                )}

                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {isLoadingModel
                            ? "Loading model…"
                            : loadError
                            ? loadError
                            : modelName
                            ? modelName
                            : "No model loaded"}
                    </p>
                    <div style={{ display: "flex", gap: 6 }}>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={generateSilhouette}
                            disabled={!model || isGenerating}
                        >
                            {isGenerating ? "Generating…" : "Generate Silhouette"}
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={handleDownload}
                            disabled={!canExport}
                        >
                            Download SVG
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
